import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Calendar, Shield, AlertTriangle } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <FileText className="h-8 w-8 text-primary-400" />
              <h1 className="text-4xl font-bold gradient-text">Terms and Conditions</h1>
            </div>
            <p className="text-xl text-gray-400">
              Please read these terms carefully before using our service
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              <span>Last updated: September 26, 2025</span>
            </div>
          </div>

          {/* Content Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-800 rounded-lg p-8 shadow-xl"
          >
            <div className="prose prose-invert max-w-none space-y-8">
              
              {/* 1. Acceptance of Terms */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-green-400" />
                  <span>1. Acceptance of Terms</span>
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  By accessing and using CS2 Skins Ranking ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              {/* 2. Description of Service */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  CS2 Skins Ranking is a web application that provides ranking and statistical information about Counter-Strike 2 (CS2) weapon skins based on professional player usage data. The service aggregates data from professional matches and provides insights into skin popularity and usage patterns.
                </p>
              </section>

              {/* 3. Data Collection and Usage */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Data Collection and Usage</h2>
                <ul className="text-gray-300 space-y-2 list-disc pl-6">
                  <li>We collect publicly available data from professional CS2 matches and tournaments</li>
                  <li>We may use cookies and similar technologies to enhance user experience</li>
                  <li>We do not collect personal information unless voluntarily provided</li>
                  <li>Data is used solely for the purpose of providing ranking and statistical services</li>
                </ul>
              </section>

              {/* 4. User Conduct */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. User Conduct</h2>
                <p className="text-gray-300 leading-relaxed mb-4">You agree not to:</p>
                <ul className="text-gray-300 space-y-2 list-disc pl-6">
                  <li>Use the service for any unlawful purposes</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the service or servers</li>
                  <li>Use automated scripts or bots to access the service excessively</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </section>

              {/* 5. Intellectual Property */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Intellectual Property</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  The service and its original content, features, and functionality are owned by CS2 Skins Ranking and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Counter-Strike 2 and related trademarks are property of Valve Corporation.
                </p>
              </section>

              {/* 6. Third-Party Services */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Third-Party Services</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Our service may contain links to third-party websites or services (such as DMarket) that are not owned or controlled by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services.
                </p>
              </section>

              {/* 7. Disclaimer of Warranties */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
                  <AlertTriangle className="h-6 w-6 text-yellow-400" />
                  <span>7. Disclaimer of Warranties</span>
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  The information on this service is provided on an "as is" basis. To the fullest extent permitted by law, we exclude all representations, warranties, and conditions relating to our service and the use of this website. Nothing in this disclaimer will:
                </p>
                <ul className="text-gray-300 space-y-2 list-disc pl-6">
                  <li>Limit or exclude our or your liability for death or personal injury</li>
                  <li>Limit or exclude our or your liability for fraud or fraudulent misrepresentation</li>
                  <li>Limit any of our or your liabilities in any way that is not permitted under applicable law</li>
                </ul>
              </section>

              {/* 8. Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  In no event shall CS2 Skins Ranking, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                </p>
              </section>

              {/* 9. Governing Law */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Governing Law</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These terms shall be interpreted and governed by the laws of the jurisdiction in which the service is operated, without regard to its conflict of law provisions.
                </p>
              </section>

              {/* 10. Changes to Terms */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Changes to Terms</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We reserve the right, at our sole discretion, to modify or replace these terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </section>

              {/* 11. Contact Information */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. Contact Information</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions about these Terms and Conditions, please contact us through our website or support channels.
                </p>
              </section>

            </div>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center text-sm text-gray-500 p-4 bg-gray-800 rounded-lg"
          >
            <p>
              By continuing to use CS2 Skins Ranking, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
